import { unique } from '@perch-labs/warbler';
import gql from 'graphql-tag';
import { PAYMENT_METHOD_FRAGMENT } from 'pages/settings/secondary/payment-methods/payment-methods.api';
import { getPayType, getSingleProjectDetails } from './statement.utils';

export const GET_STATEMENT_PAGE_DATA = gql`
  query getStatementPageData($statementId: Int!) {
    statement(id: $statementId) {
      id
      collectAutomatically
      dueDate
      includesBundledUtilityCharge
      status
      total
      totalBeforeSolarSavings
      paymentScheduledAt
      invoiceUrl
      issuedAt
      maxDelayDate
      delayable
      refundedAmountCents
      kwh {
        total
      }
      lineItemSummary {
        headlineSolarSavingsRate
        totalDualBillingChargeCents
        totalDualBillingUtilityCreditCents
        totalOtherUtilityChargeCents
        totalOtherUtilityChargePercent
        totalSolarChargeCents
        totalSolarChargePercent
        totalSolarSavingsCents
        totalSolarSavingsPercent
        totalWithoutArcadia
      }
      lineItemsFormatted {
        name
        id
        entryType
        key
        amountCents
        sandboxed
        ... on SolarLineItem {
          beforeSavings
          savings
          savingsPercentage
          utilityStatement {
            id
          }
          vnmSolarProject {
            name
            startDate
            developerName
            paymentDetails {
              paymentAddress
              bankName
              bankAccountNumber
              bankRoutingNumber
            }
            utility {
              state {
                abbreviation
              }
            }
          }
          isDelayedCredit
          isPreCredit
          vnmSubscriptionStatus
        }
        ... on UtilityLineItem {
          hasCorrespondingSolarCharge
          utility {
            name
          }
          utilityStatement {
            id
            kwh
          }
        }
        ... on CorrectionLineItem {
          correctingStatement {
            id
            issuedAt
          }
          creditCorrection {
            productionEndDate
            productionStartDate
            serviceEndDate
            serviceStartDate
          }
        }
      }
      utilityStatements {
        id
        derivedStatementData {
          activeBillingSettings {
            billExperience
          }
        }
        pdfSource
        serviceEndDate
        serviceStartDate
        utilityAccount {
          id
          accountNumber
          displayServiceAddress
          utility {
            id
            displayName
          }
        }
      }
      hostReportItems {
        id
        dateReadingFrom
        dateReadingTo
        productionStartDate
        productionEndDate
        utilityAccount {
          id
          accountNumber
          displayServiceAddress
          utility {
            id
            displayName
          }
        }
      }
      plan {
        id
        account {
          id
          user {
            id
            email
            name
          }
        }
        billingSettingsDetails {
          collectAutomatically
          directPaymentToDeveloper
          offlineInvoicing
          paysByCheck
        }
        paymentMethod {
          ...paymentMethodData
        }
        utilityAccounts {
          id
          utility {
            id
            displayName
          }
          activeBillingSettings {
            billExperience
          }
        }
      }
      mostRecentPaymentTransaction {
        id
        createdAt
        paymentMethodDescription
      }
      paymentTransactions {
        id
        disputeId
      }
    }
  }
  ${PAYMENT_METHOD_FRAGMENT}
`;

/*
  TODOs:
  - need delayable boolean on statement (define in gryphon/EP)
  - need utility name on certain line items
  - vnmSubscription.utilityAccount non-nullable? (update schema)
  - statement.plan be non-nullable? (update schema)
  - strip nested fields (statement, plan, etc)?
*/

export const parseStatementPageData = (data: GetStatementPageDataQuery) => {
  const { statement } = data;
  if (!statement) throw new Error('Missing statement');
  if (!statement.plan) throw new Error('Missing plan');

  // just used for headers - e.g. Your April bill
  const primaryDate: string = statement.issuedAt;

  // when statement was or will be paid
  const paymentDate: string =
    statement.mostRecentPaymentTransaction?.createdAt ||
    statement.paymentScheduledAt ||
    statement.dueDate;

  const utilityNames = unique(
    [
      ...statement.utilityStatements.map(us => us.utilityAccount.utility),
      ...statement.plan.utilityAccounts.map(ua => ua.utility),
    ]
      .filter(Boolean)
      .map(u => u.displayName)
  );

  const billExperiences = unique(
    [
      ...statement.utilityStatements.map(
        us => us.derivedStatementData?.activeBillingSettings?.billExperience
      ),
    ].filter((be): be is BillExperience => !!be)
  );
  // fallback to the utility account the bill experience if there wasn't one on the utility statement
  // (dual billing, statements before derived statement data)
  if (billExperiences.length === 0) {
    billExperiences.push(
      ...unique(
        [
          ...statement.plan.utilityAccounts.map(
            ua => ua.activeBillingSettings?.billExperience
          ),
        ].filter((be): be is BillExperience => !!be)
      )
    );
  }

  const solarLineItems = statement.lineItemsFormatted
    .filter(li => li.key === 'vnm_solar_charge')
    .map(li => li as SolarLineItem);
  const preCreditSolarLineItems = solarLineItems.filter(li => li.isPreCredit);
  const delayedCreditSolarLineItems = solarLineItems.filter(
    li => li.isDelayedCredit
  );
  const hasSolarCharge = solarLineItems.length > 0;

  return {
    billExperiences,
    hasSolarCharge,
    delayedCreditSolarLineItems,
    preCreditSolarLineItems,
    paymentDate,
    paymentType: getPayType({
      includesBundledUtilityCharge: statement.includesBundledUtilityCharge,
      ...statement.plan.billingSettingsDetails,
    }),
    planPaymentMethod: statement.plan.paymentMethod,
    paidMethodDescription:
      statement.mostRecentPaymentTransaction?.paymentMethodDescription,
    plan: statement.plan,
    primaryDate,
    primaryUtilityName: utilityNames[0],
    projectDetails: getSingleProjectDetails(statement.lineItemsFormatted),
    solarLineItems,
    statement,
    user: statement.plan.account.user,
    utilityNames: utilityNames.join(' & '),
    utilityStatements: statement.utilityStatements,
    hostReportItems: statement.hostReportItems,
  };
};
interface StatementPageDataMock extends GetStatementPageDataQuery {
  statement: NonNullable<GetStatementPageDataQuery['statement']>;
}
export const getStatementPageDataMock: () => StatementPageDataMock = () => ({
  statement: {
    id: 4444519,
    collectAutomatically: true,
    dueDate: '2023-04-25',
    status: 'paid',
    total: 23980,
    totalBeforeSolarSavings: 24756,
    paymentScheduledAt: '2023-04-25',
    includesBundledUtilityCharge: false,
    invoiceUrl: 'https://invoice.com',
    issuedAt: '2023-04-23',
    maxDelayDate: '2023-05-01',
    delayable: false,
    refundedAmountCents: 0,
    kwh: {
      total: 800,
    },
    lineItemSummary: {
      headlineSolarSavingsRate: 5,
      totalDualBillingChargeCents: 0,
      totalDualBillingUtilityCreditCents: 0,
      totalOtherUtilityChargeCents: 0,
      totalOtherUtilityChargePercent: 0,
      totalSolarChargeCents: 20902,
      totalSolarChargePercent: 95,
      totalSolarSavingsCents: 1100,
      totalSolarSavingsPercent: 5,
      totalWithoutArcadia: 22002,
    },
    lineItemsFormatted: [
      {
        name: 'Community Solar Farm',
        id: '7820435',
        entryType: 'debit',
        key: 'vnm_solar_charge',
        amountCents: 20902,
        beforeSavings: 22002,
        savings: 1100,
        savingsPercentage: 0.05,
        utilityStatement: {
          id: 21202620,
        },
        vnmSolarProject: {
          name: 'Fake Solar Project',
          startDate: '2022-01-01',
          utility: {
            state: {
              abbreviation: 'MD',
            },
          },
        },
        vnmSubscriptionStatus: 'waitlist',
        isDelayedCredit: false,
        isPreCredit: true,
        sandboxed: false,
      },
      {
        name: 'Pepco Utility',
        id: '7820436',
        entryType: 'debit',
        key: 'local_utility_charge',
        amountCents: 0,
        hasCorrespondingSolarCharge: true,
        utility: {
          name: 'PEPCO MD',
        },
        utilityStatement: {
          id: 21202620,
          kwh: 1157,
        },
        sandboxed: false,
      },
      {
        name: 'Wind Energy',
        id: '7820434',
        entryType: 'debit',
        key: 'consolidated_wind_energy_charge',
        amountCents: 0,
        sandboxed: false,
        __typename: 'OtherLineItem',
      },
    ],
    plan: {
      id: 21893,
      account: {
        id: 999041,
        user: {
          id: 994374,
          email: 'email994374@example.org',
          name: 'Timothy Z',
        },
      },
      billingSettingsDetails: {
        collectAutomatically: true,
        directPaymentToDeveloper: false,
        offlineInvoicing: false,
        paysByCheck: false,
      },
      paymentMethod: {
        __typename: 'PaymentMethod', // fragments need typename
        id: 249005,
        expirationDate: '2026-10',
        source: 'MasterCard',
        type: 'credit_card',
        description: 'MasterCard 2557',
      },
      utilityAccounts: [
        {
          id: 998707,
          utility: {
            id: 2287,
            displayName: 'Pepco',
          },
          activeBillingSettings: {
            billExperience: 'arcadia_billed',
          },
        },
      ],
    },
    utilityStatements: [
      {
        id: 20844437,
        derivedStatementData: {
          activeBillingSettings: {
            billExperience: 'arcadia_billed',
          },
        },
        pdfSource: 'https://example.org',
        serviceEndDate: '2023-04-19',
        serviceStartDate: '2023-03-21',
        utilityAccount: {
          id: 998707,
          accountNumber: '55016017240',
          displayServiceAddress: '9110 Emory Grove Rd, Gaithersburg, MD 20877',
          utility: {
            id: 2287,
            displayName: 'Pepco',
          },
        },
      },
    ],
    hostReportItems: [
      {
        id: 20844437,
        dateReadingFrom: '2023-03-21',
        dateReadingTo: '2023-04-19',
        productionStartDate: '2022-01-01',
        productionEndDate: '2022-12-31',
        utilityAccount: {
          id: 998707,
          accountNumber: '55016017240',
          displayServiceAddress: '9110 Emory Grove Rd, Gaithersburg, MD 20877',
          utility: {
            id: 2287,
            displayName: 'Pepco',
          },
        },
      },
    ],
    mostRecentPaymentTransaction: {
      id: 4492883,
      createdAt: '2023-04-25T20:01:16.000Z',
      paymentMethodDescription: 'visa 1234',
    },
    paymentTransactions: [
      {
        id: 4492883,
        disputeId: null,
      },
    ],
  },
});
